import { inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { EmptyFeatureResult, SignalStoreFeature, signalStoreFeature, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, pipe, switchMap, tap } from 'rxjs';

import { reconnectToWebsocketIsSafe } from '@core/stores/websocket/websocket.actions';

type ReloadMethodKey<Name extends string> = `${Name}ReloadOnReconnect`;
type ReloadOnReconnectMethods<Name extends string> = { [K in ReloadMethodKey<Name>]: () => void };

/**
 * Reusable signal-store feature for "reload a resource whenever the websocket reconnects". Given a
 * `name`, the `{name}LoadStart$` subject produced by `withApiResource(name, ...)` for the SAME name
 * (to remember the last input a load was triggered with), and that feature's `{name}Load` method,
 * produces a `{name}ReloadOnReconnect()` method that must be called explicitly to activate — not
 * every resource wants reload-on-reconnect, so it's opt-in per consumer, same as
 * `withResourceWebsocket`'s `{name}StartWebsocket()`. Built on `rxMethod`, so both internal
 * subscriptions are automatically torn down when the store is destroyed.
 *
 * Depends on `withApiResource(name, ...)` being composed on the same store first; wire it in via
 * `withFeature` for the same reason described in `with-resource-websocket.feature.ts`:
 *
 * ```ts
 * withFeature((store) => withReloadOnReconnect('thing', store.thingLoadStart$, store.thingLoad, store.clear$))
 * ```
 *
 * `clear$` is a manual "stop everything" signal (e.g. `withCancellation('clear')` composed earlier
 * on the same store) for stores that aren't reliably destroyed when their owning screen is. Unlike
 * `withResourceWebsocket`, there's no ongoing subscription to cancel here (the reconnect listener is
 * meant to keep working forever) — `clear$` just resets the tracked last input back to "no load has
 * happened yet", so a reconnect arriving between a `clear()` and the next `{name}Load(...)` doesn't
 * replay a stale input.
 */
export function withReloadOnReconnect<Name extends string, Input>(
  name: Name,
  loadStart$: Observable<Input>,
  load: (input: Input) => void,
  clear$: Observable<void>
): SignalStoreFeature<EmptyFeatureResult, { state: object; props: object; methods: ReloadOnReconnectMethods<Name> }> {
  const methodKey = `${name}ReloadOnReconnect` as ReloadMethodKey<Name>;

  return signalStoreFeature(
    withMethods(() => {
      const actions$ = inject(Actions);
      let lastInput: Input | undefined;
      let hasLoadedOnce = false;
      let started = false;

      const trackLastInput = rxMethod<Input>(
        pipe(
          tap((input) => {
            lastInput = input;
            hasLoadedOnce = true;
          })
        )
      );

      const watchReconnect = rxMethod<void>(
        pipe(
          switchMap(() => actions$.pipe(ofType(reconnectToWebsocketIsSafe))),
          tap(() => {
            if (hasLoadedOnce) {
              load(lastInput as Input);
            }
          })
        )
      );

      const resetOnClear = rxMethod<void>(
        pipe(
          tap(() => {
            lastInput = undefined;
            hasLoadedOnce = false;
          })
        )
      );

      const startReloadOnReconnect = (): void => {
        if (started) {
          return;
        }
        started = true;
        trackLastInput(loadStart$);
        watchReconnect(undefined);
        resetOnClear(clear$);
      };

      return { [methodKey]: startReloadOnReconnect } as ReloadOnReconnectMethods<Name>;
    })
  );
}
